import React, { useEffect, useState, useMemo } from "react";
import api, { ROLE_LABELS } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, User as UserIcon, Shield, CheckCircle2, XCircle, Minus, Users, HardHat } from "lucide-react";
import Colaboradores from "@/pages/Colaboradores";
import { SendWhatsAppButton } from "@/components/SendWhatsAppButton";
import CarnetConducirForm from "@/components/CarnetConducirForm";

const ALL_ROLES = ["admin", "comercial", "diseño", "produccion", "administracion", "montador", "logistica"];

const emptyForm = {
  email: "", name: "", phone: "", roles: [], password: "", active: true,
  permisos_extra: [], permisos_revocados: [],
  fecha_nacimiento: "", cumple_show: true,
  carnet_conducir: null,
};

export default function UsuariosPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setErr(""); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...emptyForm, ...u, password: "" }); setErr(""); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      if (editing) {
        const payload = {
          name: form.name, phone: form.phone, roles: form.roles, active: form.active,
          exento_fichaje: !!form.exento_fichaje,
          horario_inicio: form.horario_inicio || null,
          horario_fin: form.horario_fin || null,
          pausa_min: Number.isFinite(parseInt(form.pausa_min, 10)) ? parseInt(form.pausa_min, 10) : 0,
          dni_nie: form.dni_nie || null,
          iban: form.iban || null,
          num_seguridad_social: form.num_seguridad_social || null,
          fecha_nacimiento: form.fecha_nacimiento || null,
          cumple_show: !!form.cumple_show,
          permisos_extra: form.permisos_extra || [],
          permisos_revocados: form.permisos_revocados || [],
          carnet_conducir: form.carnet_conducir || null,
        };
        // Sólo enviamos email si cambió (evita 409 contra sí mismo en backends antiguos)
        if (form.email && form.email.toLowerCase() !== (editing.email || "").toLowerCase()) {
          payload.email = form.email.toLowerCase().trim();
        }
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      setOpen(false); load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al guardar");
    }
  };

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader
        title="Equipo y Colaboradores"
        subtitle="Usuarios internos con roles · Colaboradores externos / autónomos"
      />

      <Tabs defaultValue="equipo" className="w-full">
        <TabsList className="bg-zinc-900/60 border border-zinc-800 mb-4">
          <TabsTrigger value="equipo" data-testid="tab-equipo"
            className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300">
            <Users className="w-4 h-4 mr-1.5" /> Equipo interno ({items.length})
          </TabsTrigger>
          <TabsTrigger value="externos" data-testid="tab-externos"
            className="data-[state=active]:bg-violet-500/15 data-[state=active]:text-violet-300">
            <HardHat className="w-4 h-4 mr-1.5" /> Colaboradores externos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="equipo" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openCreate} data-testid="new-user-btn"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              <Plus className="w-4 h-4 mr-2" /> Nuevo usuario
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((u) => (
          <div key={u.id} data-testid={`user-card-${u.id}`}
            className={`bg-zinc-900/50 border rounded-xl p-5 transition-colors ${u.active ? "border-zinc-800 hover:border-amber-500/40" : "border-zinc-800 opacity-60"}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-zinc-900 font-bold">
                {(u.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-base font-semibold truncate">{u.name}</div>
                <div className="text-xs text-zinc-500 truncate">{u.email}</div>
              </div>
              {!u.active && <span className="text-[10px] uppercase text-red-400">Inactivo</span>}
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(u.roles || []).map((r) => (
                <span key={r} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                  {ROLE_LABELS[r] || r}
                </span>
              ))}
            </div>
            {u.phone && (
              <div className="flex items-center gap-2 text-xs text-zinc-400 mb-3">
                <span>{u.phone}</span>
                {u.whatsapp_number && (
                  <SendWhatsAppButton
                    to={u.whatsapp_number}
                    nombreDestinatario={u.name}
                    size="xs"
                    variant="ghost"
                    iconOnly
                    testId={`send-wa-user-${u.id}`}
                  />
                )}
              </div>
            )}
            {!u.phone && u.whatsapp_number && (
              <div className="flex items-center gap-2 text-xs text-zinc-400 mb-3">
                <span className="font-mono text-emerald-400">+{u.whatsapp_number}</span>
                <SendWhatsAppButton
                  to={u.whatsapp_number}
                  nombreDestinatario={u.name}
                  size="xs"
                  variant="ghost"
                  iconOnly
                  testId={`send-wa-user-${u.id}`}
                />
              </div>
            )}
            <div className="flex gap-2 pt-3 border-t border-zinc-800">
              <Button size="sm" variant="ghost" onClick={() => openEdit(u)} className="text-zinc-400 hover:text-amber-300 h-7 px-2">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
              </Button>
              <Button size="sm" variant="ghost"
                onClick={async () => { if (confirm(`¿Eliminar ${u.name}?`)) { await api.delete(`/users/${u.id}`); load(); } }}
                className="text-zinc-500 hover:text-red-400 h-7 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
          </div>
        </TabsContent>

        <TabsContent value="externos">
          <Colaboradores embedded />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nombre *</Label>
                <Input required value={form.name} data-testid="form-user-name"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Email *</Label>
                <Input required type="email" value={form.email}
                  data-testid="form-user-email"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-900 border-zinc-800" />
                {editing && (
                  <div className="text-[10px] text-amber-300/80 mt-1">
                    ⚠️ Si cambias el email, el usuario deberá usarlo en su próximo login.
                  </div>
                )}
              </div>
              <div><Label>Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Contraseña {editing && <span className="text-zinc-500 text-xs">(opcional)</span>}</Label>
                <Input type="password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editing ? "Dejar en blanco para no cambiar" : "Por defecto: dnf2026"}
                  className="bg-zinc-900 border-zinc-800" /></div>
              <div className="col-span-2">
                <Label>Roles</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {ALL_ROLES.map((r) => {
                    const sel = form.roles.includes(r);
                    return (
                      <button key={r} type="button"
                        onClick={() => setForm({ ...form, roles: sel
                          ? form.roles.filter((x) => x !== r)
                          : [...form.roles, r] })}
                        className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                          sel ? "bg-amber-500/20 border-amber-500/50 text-amber-200"
                              : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200"
                        }`}>
                        {ROLE_LABELS[r] || r}
                      </button>
                    );
                  })}
                </div>
              </div>
              {editing && (
                <div className="col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="active" checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="accent-amber-500" />
                  <Label htmlFor="active" className="cursor-pointer">Usuario activo</Label>
                </div>
              )}
              {editing && (
                <>
                  <div className="col-span-2 border-t border-zinc-800 pt-3">
                    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">⏱️ Fichaje horario</div>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <input type="checkbox" id="exento" checked={!!form.exento_fichaje}
                      onChange={(e) => setForm({ ...form, exento_fichaje: e.target.checked })}
                      className="accent-amber-500" data-testid="form-user-exento" />
                    <Label htmlFor="exento" className="cursor-pointer">
                      Exento de fichaje (autónomo / no obligado)
                    </Label>
                  </div>
                  <div>
                    <Label className="text-xs">Horario inicio</Label>
                    <Input type="time" value={form.horario_inicio || ""}
                      onChange={(e) => setForm({ ...form, horario_inicio: e.target.value })}
                      placeholder="07:00"
                      className="bg-zinc-900 border-zinc-800" data-testid="form-user-h-inicio" />
                  </div>
                  <div>
                    <Label className="text-xs">Horario fin</Label>
                    <Input type="time" value={form.horario_fin || ""}
                      onChange={(e) => setForm({ ...form, horario_fin: e.target.value })}
                      placeholder="15:00"
                      className="bg-zinc-900 border-zinc-800" data-testid="form-user-h-fin" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Pausa no pagada (min/día) · se resta de las horas esperadas. 0 = pausa pagada</Label>
                    <Input type="number" min="0" step="5" value={form.pausa_min ?? 0}
                      onChange={(e) => setForm({ ...form, pausa_min: e.target.value })}
                      className="bg-zinc-900 border-zinc-800 w-32" data-testid="form-user-pausa-min" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">DNI / NIE (para matching automático de nóminas)</Label>
                    <Input value={form.dni_nie || ""}
                      onChange={(e) => setForm({ ...form, dni_nie: e.target.value.toUpperCase() })}
                      placeholder="12345678A"
                      className="bg-zinc-900 border-zinc-800 font-mono" data-testid="form-user-dni" />
                  </div>
                  <div>
                    <Label className="text-xs">IBAN cuenta bancaria</Label>
                    <Input value={form.iban || ""}
                      onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase().replace(/\s+/g, "") })}
                      placeholder="ES00 0000 0000 0000 0000 0000"
                      className="bg-zinc-900 border-zinc-800 font-mono" data-testid="form-user-iban" />
                  </div>
                  <div>
                    <Label className="text-xs">Nº Seguridad Social</Label>
                    <Input value={form.num_seguridad_social || ""}
                      onChange={(e) => setForm({ ...form, num_seguridad_social: e.target.value.replace(/\s+/g, "") })}
                      placeholder="0000000000"
                      className="bg-zinc-900 border-zinc-800 font-mono" data-testid="form-user-nss" />
                  </div>

                  {/* 🚗 Carnet de conducir */}
                  <div className="col-span-2 border-t border-zinc-800 pt-3">
                    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">🚗 Carnet de conducir</div>
                    <CarnetConducirForm
                      value={form.carnet_conducir}
                      onChange={(c) => setForm({ ...form, carnet_conducir: c })}
                    />
                  </div>

                  {/* 🎂 Cumpleaños */}
                  <div className="col-span-2 bg-pink-500/5 border border-pink-500/30 rounded-lg p-3 grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-pink-200">🎂 Fecha de nacimiento</Label>
                      <Input type="date" value={form.fecha_nacimiento || ""}
                        onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })}
                        className="bg-zinc-900 border-zinc-800 mt-1"
                        data-testid="form-user-fecha-nacimiento" />
                      <div className="text-[10px] text-zinc-500 mt-1">El equipo recibirá un aviso amable el día de tu cumple.</div>
                    </div>
                    <div className="flex items-center gap-2 mt-6">
                      <input type="checkbox" id="cumple_show" checked={!!form.cumple_show}
                        onChange={(e) => setForm({ ...form, cumple_show: e.target.checked })}
                        className="accent-pink-500 w-4 h-4" />
                      <Label htmlFor="cumple_show" className="text-xs cursor-pointer">
                        Visible al equipo (avisar a tus compañeros para que te feliciten)
                      </Label>
                    </div>
                  </div>

                  <div className="col-span-2 border-t border-zinc-800 pt-3">
                    <PermissionsMatrix roles={form.roles}
                                       extras={form.permisos_extra || []}
                                       revocados={form.permisos_revocados || []}
                                       onChange={(extras, revocados) => setForm({ ...form, permisos_extra: extras, permisos_revocados: revocados })} />
                  </div>
                </>
              )}
              {err && <div className="col-span-2 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">{err}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-zinc-700">Cancelar</Button>
              <Button type="submit" data-testid="form-user-submit" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============ PERMISSIONS MATRIX (RBAC fino) ============
function PermissionsMatrix({ roles, extras, revocados, onChange }) {
  const [catalog, setCatalog] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});

  useEffect(() => {
    api.get("/permissions/catalog")
      .then((r) => {
        setCatalog(r.data?.catalog || []);
        setRoleDefaults(r.data?.role_defaults || {});
      })
      .catch(() => {});
  }, []);

  // Permisos heredados de los roles del usuario
  const heredados = useMemo(() => {
    const set = new Set();
    for (const r of roles || []) {
      for (const p of roleDefaults[r] || []) set.add(p);
    }
    return set;
  }, [roles, roleDefaults]);

  const isWildcard = heredados.has("*");

  // Agrupar por módulo
  const grupos = useMemo(() => {
    const m = {};
    for (const p of catalog) {
      (m[p.modulo] = m[p.modulo] || []).push(p);
    }
    return m;
  }, [catalog]);

  const estadoEfectivo = (slug) => {
    if (revocados.includes(slug)) return "revocado";
    if (extras.includes(slug) || extras.includes("*")) return "extra";
    if (heredados.has(slug) || isWildcard) return "heredado";
    return "no";
  };

  const ciclar = (slug) => {
    const est = estadoEfectivo(slug);
    const newExtras = extras.filter((x) => x !== slug);
    const newRevocados = revocados.filter((x) => x !== slug);
    const heredado = heredados.has(slug) || isWildcard;

    if (heredado) {
      // heredado → revocado → heredado
      if (est === "heredado") newRevocados.push(slug);
      // si era revocado → quitarlo (vuelve a heredado)
    } else {
      // no → extra → no
      if (est === "no") newExtras.push(slug);
    }
    onChange(newExtras, newRevocados);
  };

  if (!catalog.length) return null;

  return (
    <div data-testid="permissions-matrix">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-amber-400" />
        <div className="text-xs uppercase tracking-wider text-amber-400/80">Permisos finos (override)</div>
      </div>
      <div className="text-[11px] text-zinc-500 mb-3">
        Por defecto el usuario hereda los permisos de sus roles. Aquí puedes <b className="text-emerald-400">conceder extra</b> (permisos que su rol no incluye) o <b className="text-rose-400">revocar</b> (quitar un permiso heredado del rol).
      </div>
      <div className="space-y-3">
        {Object.entries(grupos).map(([modulo, perms]) => (
          <div key={modulo} className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">{modulo}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {perms.map((p) => {
                const est = estadoEfectivo(p.slug);
                const colors = {
                  heredado: "border-emerald-700/40 bg-emerald-500/5 text-emerald-300",
                  extra:    "border-amber-700/40 bg-amber-500/10 text-amber-300",
                  revocado: "border-rose-700/40 bg-rose-500/10 text-rose-300 line-through",
                  no:       "border-zinc-800 text-zinc-500",
                };
                const Icon = {
                  heredado: CheckCircle2, extra: Plus, revocado: XCircle, no: Minus,
                }[est];
                return (
                  <button key={p.slug} type="button" onClick={() => ciclar(p.slug)}
                          data-testid={`perm-${p.slug}`}
                          title={p.desc}
                          className={`flex items-start gap-2 text-left text-xs px-2.5 py-1.5 rounded border transition ${colors[est]}`}>
                    <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium">{p.label}</div>
                      <div className="text-[10px] opacity-70 font-mono">{p.slug}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-zinc-500">
        <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> heredado del rol</span>
        <span className="flex items-center gap-1"><Plus className="w-3 h-3 text-amber-400" /> concedido extra</span>
        <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-rose-400" /> revocado</span>
        <span className="flex items-center gap-1"><Minus className="w-3 h-3 text-zinc-500" /> sin acceso</span>
      </div>
    </div>
  );
}
